import { Button, Card, Col, Divider, Dropdown, Modal, Row, Space, Tooltip, Typography } from 'antd';
import {
    CheckCircleFilled, DeleteOutlined, EditOutlined, EllipsisOutlined,
    ExclamationCircleOutlined, PlusCircleOutlined
} from '@ant-design/icons';
import { useEffect, useState } from 'react';
import GenericTable from '../../../components/Commons/Table/generic-table.component';
import { useMutation, useQuery } from 'react-query';
import ConfirmModal from '../../../components/Commons/Modal/confirm-modal.component';
import { getAll, remove } from '../../../services/bookingServices.service';
import ProfilePermissionService from '../../../services/profilePermissions.service';
import { getAll as loadServiceTypes } from '../../../services/serviceTypes.service';
import { getAll as loadServiceStates } from '../../../services/serviceStates.service';
import { getAll as loadVehicles } from '../../../services/vehicles.service';
import { getAll as loadWorkers } from '../../../services/workers.service';
import { all as loadZones } from '../../../services/zones.service';
import { getAll as loadLocations } from '../../../services/locations.service';
import { AlertService } from '../../../services/alert.service';
import ShowDataLength from '../../../components/Commons/Table/results-length-table.component';
import moment from 'moment';
import FormService from './Form';
import DisplayAlert from '../../../components/Commons/Alert';
import { cutLongText, downloadExcel, formatCurrency, formatCurrencyToExport } from '../../../services/utils';
import { isMobile } from 'react-device-detect';
import ExportBtn from '../../../components/Commons/Export/ExportButton';
import { EnumCharges } from '../../../Enums/Charges.enums';

const { Title, Text } = Typography;

interface PropsServices {
    bookingId?: any,
    booking?: any,
    totalServices: Function,
    firstServiceCreated?: Function,
    operatorId?: any
}

export default function ListServices({ bookingId, booking, totalServices, firstServiceCreated, operatorId }: PropsServices) {
    const [services, setServices] = useState([]);
    const [exportIsLoading, setExportIsLoading] = useState<boolean>(false);
    const [openServiceModalForm, setOpenServiceModalForm] = useState<boolean>(false);
    const [removeService, setRemoveService] = useState<any | undefined>(undefined);
    const [serviceToEdit, setServiceToEdit] = useState<any>();
    const [createReturnService, setCreateReturnService] = useState<any>();
    const [modalFormTitle, setModalFormTitle] = useState<string>('');

    const profilePermissionService = ProfilePermissionService.getInstance();

    const settings = {
        create: { title: 'Novo serviço' },
        edit: { title: 'Editar serviço' },
        duplicate: { title: 'Duplicar serviço' },
        createReturn: { title: 'Criar retorno' },
        editReturn: { title: 'Editar retorno' }
    };

    const carTypes = {
        small: 'Carro pequeno',
        large: 'Carro grande'
    };

    const charges = EnumCharges;

    const { isLoading: servicesLoading, refetch: loadServices } = useQuery(
        ['loadServices'],
        () => getAll({ booking_id: bookingId }),
        {
            onSuccess: response => {
                setServices(response.data.map((service: any) => {
                    return {
                        key: service.data.id,
                        ...service.data,
                        ...service.relationships
                    }
                }));
            },
            refetchOnWindowFocus: false,
            enabled: false
        }
    );

    const { data: serviceTypes } = useQuery(
        ['loadServiceTypes'],
        () => loadServiceTypes(),
        { refetchOnWindowFocus: false }
    );

    const { data: serviceStates } = useQuery(
        ['loadServiceStates'],
        () => loadServiceStates(),
        { refetchOnWindowFocus: false }
    );

    const { data: locations } = useQuery(
        ['loadLocations'],
        () => loadLocations(),
        { refetchOnWindowFocus: false }
    );

    const { data: zones } = useQuery(
        ['loadZones'],
        () => loadZones(),
        { refetchOnWindowFocus: false }
    );

    const { data: staff } = useQuery(
        ['loadStaff'],
        () => loadWorkers({ type: 'staff', status: 'active', with_relationship: '0' }),
        { refetchOnWindowFocus: false }
    );

    const { data: suppliers } = useQuery(
        ['loadSuppliers'],
        () => loadWorkers({ type: 'suppliers', status: 'active', with_relationship: '0' }),
        { refetchOnWindowFocus: false }
    );

    const { data: vehicles } = useQuery(
        ['loadVehicles'],
        () => loadVehicles(),
        { refetchOnWindowFocus: false }
    );

    const { mutate: removeServiceMutate } = useMutation(remove,
        {
            onSuccess: () => {
                AlertService.sendAlert([
                    { text: `Serviço ${removeService.booking_id}-${removeService.id} apagado com sucesso.` }
                ]);

                setRemoveService(undefined);

                if (openServiceModalForm) {
                    onCloseServiceModalForm();
                }

                loadServices();
            },
            onError: () => {
                setRemoveService(undefined);

                if (openServiceModalForm) {
                    onCloseServiceModalForm();
                }
            }
        }
    );

    const onOpenServiceModalForm = (title: string, record: any) => {
        AlertService.clearAlert();
        setServiceToEdit(record);
        setModalFormTitle(title);
        setOpenServiceModalForm(true);
    };

    const onCloseServiceModalForm = () => {
        setOpenServiceModalForm(false);
        setServiceToEdit(undefined);
    };

    const onSavedForm = (
        successMessage: string,
        newRecord: any = null,
        createReturnOrDuplicate: 'duplicate' | 'createReturn' | undefined = undefined
    ) => {
        onCloseServiceModalForm();

        if (createReturnOrDuplicate) {
            if (createReturnOrDuplicate === 'duplicate') {
                onDuplicate(newRecord);
            } else {
                onCreateReturn(newRecord);
            }
        }

        if (!services.length && firstServiceCreated) {
            firstServiceCreated();
        }

        setTimeout(() => {
            AlertService.sendAlert([
                { text: successMessage }
            ]);

            loadServices();
        }, 500);
    }

    const onDuplicate = (record: any) => {
        AlertService.clearAlert();
        record.id = null;
        record.driver_notes = null;
        onOpenServiceModalForm(settings.duplicate.title, record);
    };

    const onCreateReturn = (record: any) => {
        AlertService.clearAlert();
        record.parent = { ...record };

        record.parent_id = record.id;
        record.id = null;
        record.staff_id = null;
        record.supplier_id = null;
        record.driver_notes = null;

        let { pickup_location_id, pickup_location, pickup_zone, pickup_zone_id, pickup_address, pickup_reference_point,
            dropoff_location_id, dropoff_location, dropoff_zone, dropoff_zone_id, dropoff_address, dropoff_reference_point
        } = record;

        record.pickup_location_id = dropoff_location_id;
        record.pickup_location = dropoff_location;
        record.pickup_zone_id = dropoff_zone_id;
        record.pickup_zone = dropoff_zone;
        record.pickup_address = dropoff_address;
        record.pickup_reference_point = dropoff_reference_point;
        record.dropoff_location_id = pickup_location_id;
        record.dropoff_location = pickup_location;
        record.dropoff_zone_id = pickup_zone_id;
        record.dropoff_zone = pickup_zone;
        record.dropoff_address = pickup_address;
        record.dropoff_reference_point = pickup_reference_point;

        onOpenServiceModalForm(settings.createReturn.title, record);
    };

    const onMapDataToExport = (data: any) => {
        return data.map((record: any) => {
            return {
                'Tipo de serviço': record?.serviceType?.data?.name,
                'Data': record.start ? moment(record.start, 'YYYY-MM-DD').format('DD/MM/YYYY') : '',
                'Hora': record.hour || '',
                'ID Reserva': record?.booking_id,
                'ID Serviço': `${record.booking_id}-${record?.id}`,
                'Referência': record?.relationships?.booking?.data?.reference,
                'Motorista': record?.staff?.data?.name || record?.supplier?.data?.name,
                'Viatura': record?.vehicle_text,
                'Tipo de carro': record?.car_type ? carTypes[record?.car_type as keyof typeof carTypes] : '',
                'Estado': record?.serviceState?.data?.name,
                'N° Voo': record?.flight_number,
                'Pick-up': record?.pickup_location,
                'Drop-off': record?.dropoff_location,
                'Adultos': record?.number_adults,
                'Crianças': record?.number_children,
                'Cadeira de bebê': record?.number_baby_chair,
                'Booster': record?.booster,
                'Operador': record?.booking?.relationships?.operator?.data?.name,
                'Obs. Gerais': record?.notes,
                'Obs. Internas': record?.internal_notes,
                'Val. Serviço': formatCurrencyToExport(record?.value),
                'Cobrança': record?.charge ? charges[record?.charge as keyof typeof charges] : '',
                'Comissão': formatCurrencyToExport(record?.commission),
            };
        });
    };

    const exportData = () => downloadExcel(onMapDataToExport(services), `Reserva nº ${bookingId} - Serviços`);

    const columns: any = [
        {
            title: 'Número', key: 'number', width: 120,
            render: (_: any, record: any) => (<Space>
                <Text>Serviço {record.booking_id}-{record.id}</Text>
                {record.emphasis && <Tooltip placement="top" title="Ação necessária">
                    <ExclamationCircleOutlined style={{ color: '#faad14' }} />
                </Tooltip>}
            </Space>),
            responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
        },
        {
            title: 'Data', key: 'start', dataIndex: 'start', sorter: true,
            render: (_: any, record: any) => record.start ? moment(record.start, 'YYYY-MM-DD').format('DD/MM/YYYY') : '',
            responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
        },
        {
            title: 'Hora', key: 'hour', dataIndex: 'hour', sorter: true,
            render: (_: any, record: any) => record.hour ? moment(record.hour, 'H:mm:ss').format('HH:mm') : '',
            responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
        },
        {
            title: 'Pick-up', key: 'pickup_location', width: 200, dataIndex: 'pickup_location',
            render: (_: any, record: any) => (cutLongText(record?.pickup_location, 20)),
            responsive: ['lg', 'xl'],
        },
        {
            title: 'Drop-off', key: 'dropoff_location', width: 200, dataIndex: 'dropoff_location',
            render: (_: any, record: any) => (cutLongText(record?.dropoff_location, 20)),
            responsive: ['lg', 'xl'],
        },
        {
            title: 'Tipo', key: 'service_type_id', dataIndex: 'service_type_id',
            render: (_: any, record: any) => (record?.serviceType?.data?.name),
            responsive: ['lg', 'xl'],
        },
        {
            title: 'Staff/Fornecedor', key: 'staff_id', dataIndex: 'staff_id', width: 170,
            render: (_: any, record: any) => (record?.staff?.data?.name || record?.supplier?.data?.name),
            responsive: ['lg', 'xl'],
        },
        {
            title: 'Valor', key: 'value', dataIndex: 'value',
            render: (_: any, record: any) => (record?.value ? formatCurrency(record?.value) : ''),
            responsive: ['lg', 'xl'],
        },
        {
            title: 'Ações', key: 'action', width: 100,
            render: (_: any, record: any) => (
                profilePermissionService.hasPermission(`bookings-services:write`) &&
                <Space size="small" direction={isMobile ? 'vertical' : 'horizontal'}>
                    <Tooltip placement="top" title="Editar">
                        <Button
                            type="default" shape="circle" size="middle" icon={<EditOutlined />}
                            onClick={() => onOpenServiceModalForm(
                                settings.edit.title,
                                { ...record }
                            )} />
                    </Tooltip>

                    <Tooltip placement="top" title="Apagar">
                        <Button type="default" shape="circle" size="middle" icon={<DeleteOutlined />}
                            onClick={() => setRemoveService(record)} />
                    </Tooltip>

                    <Dropdown menu={{
                        items: [
                            {
                                key: 'duplicate', label: 'Duplicar serviço',
                                onClick: () => onDuplicate({ ...record })
                            },
                            {
                                key: 'createReturn', label: 'Criar retorno',
                                onClick: () => onCreateReturn({ ...record })
                            }
                        ]
                    }} placement="bottomRight" arrow>
                        <Tooltip placement="top" title="Mais ações">
                            <Button type="default" shape="circle" size="middle" icon={<EllipsisOutlined />} />
                        </Tooltip>
                    </Dropdown>
                </Space>
            )
        }
    ];

    const formService = (
        <FormService bookingId={bookingId} service={serviceToEdit}
            isParent={false} showBookingData={true}
            closeForm={onCloseServiceModalForm} savedForm={onSavedForm}
            setRemoveService={setRemoveService}
            serviceTypes={serviceTypes?.data?.map((item: any) => item.data) || []}
            serviceStates={serviceStates?.data?.map((item: any) => item.data) || []}
            staff={staff?.data || []} suppliers={suppliers?.data || []}
            vehicles={vehicles?.data?.map((item: any) => item.data) || []}
            locations={locations?.data || []}
            zones={zones?.data || []} operatorId={operatorId}
            pax={booking?.pax_group}
        />
    );

    useEffect(() => {
        if (bookingId) {
            loadServices();
        }
    }, [bookingId]);

    useEffect(() => {
        totalServices(services.length);
    }, [services.length]);

    return (
        <>
            <Title style={{ marginTop: 25, marginBottom: 25 }} level={3}>
                Serviços
                {profilePermissionService.hasPermission('bookings-services:write') &&
                    <Button type="link" color="primary" icon={<PlusCircleOutlined />}
                        style={{ fontSize: 18, fontWeight: 400 }}
                        onClick={() => onOpenServiceModalForm(settings.create.title, { booking: { data: booking } })}
                        disabled={
                            !booking?.client_name
                            || !booking?.operator_id
                            || !booking?.pax_group
                        }
                    >Associar serviço</Button>
                }
            </Title>

            <Card bodyStyle={{ padding: 8 }}>
                <div className="table">
                    <GenericTable
                        columns={columns} dataSource={services} loading={servicesLoading}
                        size='small'
                        totalChildren={
                            <div className="" style={isMobile ? {} : { marginTop: 8 }}>
                                <Row>
                                    <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 12 }} xl={{ span: 12 }}
                                        style={isMobile ? { marginBottom: 10 } : {}}>
                                        <ShowDataLength className="show_result_size" size={services.length} />
                                    </Col>

                                    <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 12 }} xl={{ span: 12 }}
                                        style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                        <ExportBtn disabled={!services.length} loading={exportIsLoading} block={isMobile}
                                            onClick={() => {
                                                setExportIsLoading(true);

                                                setTimeout(() => {
                                                    exportData();
                                                    setExportIsLoading(false);
                                                }, 1000);
                                            }} />
                                    </Col>
                                </Row>
                            </div>
                        }
                    />
                </div>

                <Modal
                    open={openServiceModalForm} width={1180} style={{ top: 20 }}
                    footer={null} maskClosable={false} closeIcon={false}>
                    <DisplayAlert />
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Text strong style={{ marginTop: 10 }}>{modalFormTitle}</Text>
                    </div>

                    <Divider style={{ marginBottom: 0 }}></Divider>

                    {formService}
                </Modal>

                <ConfirmModal
                    open={!!createReturnService}
                    icon={<CheckCircleFilled style={{ color: '#52c41a', marginRight: 10, fontSize: 18 }} />}
                    title={createReturnService?.message}
                    content="Deseja criar um serviço de retorno?"
                    okText="Criar retorno" cancelText="Continuar sem retorno"
                    onOk={() => {
                        onCreateReturn(createReturnService?.data);
                        setCreateReturnService(undefined);
                    }}
                    onCancel={() => setCreateReturnService(undefined)}
                ></ConfirmModal>

                <ConfirmModal
                    open={!!removeService} zIndex={2000}
                    title={`Tem certeza que deseja apagar o serviço "${bookingId}-${removeService?.id}"?`}
                    okText="Apagar" cancelText="Voltar"
                    onOk={() => removeServiceMutate(removeService?.id)}
                    onCancel={() => setRemoveService(undefined)}
                ></ConfirmModal>
            </Card>
        </>
    )
}